import React, { ChangeEvent } from "react";
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";

import MoovsDialog from "components/globals/MoovsDialog";
import ConsentSignatureFooter from "./ConsentSignatureFooter";
import { CreateReservationFormState } from "pages/new/steps/confirm/components/CreateReservationForm/form/schemaValidation";
import { ConfirmReservationFormState } from "pages/order/components/ConfirmReservationForm/form/schemaValidation";
import { TermsSignatureFormState } from "../../termsSignatureSchema";
import { TermsAndConditionsBody } from "..";
import { useCancellationPolicies } from "../../hooks/useCancellationPolicies";
import { deriveStrictestCancellationPolicy } from "pages/new/utils/deriveStrictestCancellationPolicy";

type ConsentSignatureDialogProps = {
  control: Control<
    | CreateReservationFormState
    | ConfirmReservationFormState
    | TermsSignatureFormState
  >;
  formId: string;
  watch: UseFormWatch<
    | CreateReservationFormState
    | ConfirmReservationFormState
    | TermsSignatureFormState
  >;
  setValue: UseFormSetValue<any>;
  isSubmitting: boolean;
  isDialogOpen: boolean;
  onClose: () => void;
  consentSignature: string;
  userConsentAgreement: boolean;
  onConsentSignatureChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onUserConsentAgreementToggle: () => void;
  selectedVehicleIds: string[];
  // Add a new prop for handling signatures
  onSignatureSubmit?: (signature: string) => void;
};

function ConsentSignatureDialog(props: ConsentSignatureDialogProps) {
  const {
    control,
    formId,
    watch,
    setValue,
    isSubmitting,
    isDialogOpen,
    onClose,
    consentSignature,
    userConsentAgreement,
    onConsentSignatureChange,
    onUserConsentAgreementToggle,
    selectedVehicleIds,
  } = props;

  // hooks
  const { cancellationPolicies: cancellationPoliciesData } =
    useCancellationPolicies({ trips: null });

  // derived state
  const cancellationPolicies = deriveStrictestCancellationPolicy({
    cancellationPoliciesData,
    selectedVehicleIds,
  });
  const hasCancellationPolicy = !!cancellationPolicies.length;

  const handleCloseDialog = () => {
    setValue("isTermsAccepted", false);
    setValue("printedName", "");
    onClose();
  };

  return (
    <MoovsDialog
      dialogTitle={
        hasCancellationPolicy
          ? "Terms & Conditions, Privacy Policy and Cancellation Policy"
          : "Terms & Conditions and Privacy Policy"
      }
      open={isDialogOpen}
      onClose={handleCloseDialog}
      onAccept={onClose} // let parent formSubmit handle onAccept
      acceptButtonText="Sign Electronically"
      fixedFooter
      customFooter={
        <ConsentSignatureFooter
          control={control}
          watch={watch}
          setValue={setValue}
          formId={formId}
          isSubmitting={isSubmitting}
          onDialogClose={handleCloseDialog}
          consentSignature={consentSignature}
          userConsentAgreement={userConsentAgreement}
          onConsentSignatureChange={onConsentSignatureChange}
          onConsentAgreementClick={onUserConsentAgreementToggle}
          hasCancellationPolicy={hasCancellationPolicy}
        />
      }
    >
      <TermsAndConditionsBody selectedVehicleIds={selectedVehicleIds} />
    </MoovsDialog>
  );
}

export default ConsentSignatureDialog;
